/**
 * Typed wrappers for `publicDiagnostic*` callables (#580).
 * Lane A: start/next/submit/claim placeholders that never throw to UI.
 * Lane C: AskPam with degrade-never-throw; the simulation beat is local content.
 * @module features/publicDiagnostic/api
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { callWithTimeout } from '@/utils/callWithTimeout';
import { logger } from '@/utils/logger';
import { TURN_KIND_BY_ID } from './turnMap';
import type {
  AskPamResult,
  PublicDiagnosticHandoffPayload,
  TurnId,
  TurnResult,
} from './types';
import {
  ASK_PAM_DEGRADED_MARKDOWN,
  ASK_PAM_REFUSE_MARKDOWN,
  IP_RATE_LIMIT,
  PUBLIC_DIAGNOSTIC_BEAT,
  SESSION_RATE_LIMIT,
  buildAskPamResult,
  classifyAskPamPrompt,
  clientIpKey,
  laneCIpLimiter,
  laneCSessionLimiter,
  truncateAskPamPrompt,
  type SimulationBeatContent,
} from './laneCPolicy';

export interface PublicDiagnosticStartResponse {
  sessionId: string;
  turn1: unknown;
}

export interface PublicDiagnosticNextResponse {
  turn: unknown;
}

export interface PublicDiagnosticSubmitTurnResponse {
  ok: boolean;
  nextPrefetch?: unknown;
}

export interface AskPamRequest {
  sessionId: string;
  prompt: string;
}

export interface AskPamResponse {
  prompt: string;
  answerMarkdown: string;
  llmUsed: boolean;
  refused?: boolean;
  rateLimited?: boolean;
  degraded?: boolean;
}

/** The typing state covers the wait; past this the canned tip shows instead. */
const CALL_MS = 10_000;

function placeholderTurn(turnId: TurnId): { turnId: TurnId; kind: string } {
  return { turnId, kind: TURN_KIND_BY_ID[turnId] };
}

function allowTraffic(sessionId: string): boolean {
  const sessionOk = laneCSessionLimiter.allow(
    `sess:${sessionId}`,
    SESSION_RATE_LIMIT.max,
    SESSION_RATE_LIMIT.windowMs
  );
  const ipOk = laneCIpLimiter.allow(clientIpKey(), IP_RATE_LIMIT.max, IP_RATE_LIMIT.windowMs);
  return sessionOk && ipOk;
}

/** Client-minted session + turn-1 shell. Backend replaces this later. */
export async function publicDiagnosticStart(): Promise<PublicDiagnosticStartResponse> {
  const sessionId = crypto.randomUUID();
  return { sessionId, turn1: placeholderTurn(1) };
}

/**
 * Next-turn shell for prefetch. `turnId` is the turn being prefetched;
 * the callable name stays `publicDiagnosticNext` per contract.
 */
export async function publicDiagnosticNext(
  _sessionId: string,
  turnId: TurnId = 1
): Promise<PublicDiagnosticNextResponse> {
  return { turn: placeholderTurn(turnId) };
}

export async function publicDiagnosticSubmitTurn(
  _sessionId: string,
  _result: TurnResult
): Promise<PublicDiagnosticSubmitTurnResponse> {
  return { ok: true };
}

/** Best-effort claim at signup. Local handoff copy is the source of truth if this fails. */
export async function publicDiagnosticClaimHandoff(
  uid: string,
  payload: PublicDiagnosticHandoffPayload
): Promise<void> {
  try {
    const claim = httpsCallable(functions, 'publicDiagnosticClaimHandoff');
    // Bounded: TrialSignupFlow awaits this before onSuccess, so a hung callable must not hold signup.
    await callWithTimeout(() => claim({ uid, ...payload }), { timeoutMs: 8000, retries: 0 });
  } catch (error) {
    logger.warn('publicDiagnostic', 'claimHandoff failed (non-fatal; local handoff kept)', {
      error,
    });
    throw error;
  }
}

/** Local beat payload for Lane A mount (no network). */
export function getLocalSimulationBeat(): SimulationBeatContent {
  return PUBLIC_DIAGNOSTIC_BEAT;
}

/**
 * Ask Pam for the public diagnostic. Never throws to UI.
 * Off-topic / clinical / legal → refused redirect in Pam voice.
 */
export async function publicDiagnosticAskPam(
  sessionId: string,
  prompt: string
): Promise<AskPamResult> {
  const truncated = truncateAskPamPrompt(prompt);

  if (classifyAskPamPrompt(truncated) === 'refuse') {
    return buildAskPamResult({
      prompt: truncated,
      answerMarkdown: ASK_PAM_REFUSE_MARKDOWN,
      llmUsed: false,
      refused: true,
    });
  }

  if (!allowTraffic(sessionId)) {
    return buildAskPamResult({
      prompt: truncated,
      answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: false,
    });
  }

  try {
    const data = await callWithTimeout(
      () =>
        httpsCallable<AskPamRequest, AskPamResponse>(functions, 'publicDiagnosticAskPam')({
          sessionId,
          prompt: truncated,
        }).then((r) => r.data),
      { timeoutMs: CALL_MS, retries: 0 }
    );

    return buildAskPamResult({
      prompt: data.prompt || truncated,
      answerMarkdown: data.answerMarkdown?.trim() || ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: Boolean(data.llmUsed) && !data.degraded && !data.rateLimited && !data.refused,
      refused: data.refused,
    });
  } catch (error) {
    logger.info('publicDiagnostic', 'askPam degraded', { error });
    return buildAskPamResult({
      prompt: truncated,
      answerMarkdown: ASK_PAM_DEGRADED_MARKDOWN,
      llmUsed: false,
    });
  }
}

/** Test seams */
export function __resetLaneCLimiters(): void {
  laneCSessionLimiter.reset();
  laneCIpLimiter.reset();
}
